import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useIonRouter } from '@ionic/vue';
import { Sentry } from '@/services/sentry';
import {
  hasPremiumAccess,
  presentPaywall,
  presentCustomerCenter,
  restorePurchases,
  getCustomerInfo
} from '@/services/revenueCat';
import { useUserStore } from '@/stores/user';

/**
 * Composable for managing subscription state and actions
 * Now synced with user store for consistent state across app
 */
export const useSubscription = () => {
  const userStore = useUserStore();
  const ionRouter = useIonRouter();
  const { isPremium } = storeToRefs(userStore);
  const isLoading = computed(() => false); // Can add loading state to store if needed

  /**
   * Check if user has premium access and update store
   */
  const checkPremiumAccess = async () => {
    try {
      const premium = await hasPremiumAccess();
      userStore.setPremiumStatus(premium);
      return premium;
    } catch (error) {
      console.error('Failed to check premium access:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  /**
   * Show paywall to user
   */
  const showPaywall = async () => {
    try {
      const purchased = await presentPaywall();
      if (purchased) {
        userStore.setPremiumStatus(true);
        ionRouter.push('/purchase-success');
      }
      return purchased;
    } catch (error) {
      console.error('Failed to show paywall:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  const showCustomerCenter = async () => {
    try {
      return await presentCustomerCenter();
    } catch (error) {
      console.error('Failed to show customer center:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  /**
   * Restore user's purchases
   */
  const restore = async () => {
    try {
      const restored = await restorePurchases();
      if (restored) {
        userStore.setPremiumStatus(true);
      }
      return restored;
    } catch (error) {
      console.error('Failed to restore purchases:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  /**
   * Get full customer info
   */
  const fetchCustomerInfo = async () => {
    try {
      return await getCustomerInfo();
    } catch (error) {
      console.error('Failed to fetch customer info:', error);
      Sentry.captureException(error);
      return null;
    }
  };

  return {
    isPremium,
    isLoading,
    checkPremiumAccess,
    showPaywall,
    showCustomerCenter,
    restore,
    fetchCustomerInfo
  };
};
